import type { HelpArticle } from '../../../components/help/types';

export const pfileManagementArticles: HelpArticle[] = [
  {
    id: 'pfiles-overview',
    title: 'Personnel File (PFile) System',
    summary: 'Understanding the jacket-based personnel file system.',
    category: 'pfiles',
    order: 1,
    keywords: ['pfile', 'personnel', 'jacket', 'staff', 'employee', 'hr'],
    content: `
# Personnel File (PFile) System

The PFile system manages staff personnel records using a jacket-based approach.

## What is a PFile Jacket?

A PFile jacket is a digital representation of a staff member's physical personnel file:
- Contains all documents related to an employee
- Tracks the physical location of the file
- Maintains complete movement history
- Includes staff biographical information

## Key Components

### Staff Information
- Full name and staff ID
- Department and position
- Date of first appointment
- Contact information

### Jacket Location
- Current physical location
- Custodian information
- Movement history

### Documents
- Employment records
- Promotion letters
- Performance reviews
- Training certificates
- Disciplinary records

## PFile Statuses

- **Active**: Currently employed
- **On Leave**: Temporarily away
- **Transferred**: Moved to another MDA
- **Retired**: Left service
- **Deceased**: For record keeping

## Access Control

PFile access is restricted based on:
- User's department
- Role permissions
- Classification levels
    `,
  },
  {
    id: 'pfiles-create',
    title: 'Creating a PFile Jacket',
    summary: 'How to create a new personnel file jacket.',
    category: 'pfiles',
    order: 2,
    keywords: ['create', 'new', 'pfile', 'jacket', 'add', 'staff'],
    content: `
# Creating a PFile Jacket

Create a new personnel file jacket for a staff member.

## Before You Start

Ensure you have:
- Staff member's basic information
- Staff ID number
- Department assignment
- Required permissions

## Steps to Create

1. Navigate to **File Jackets**
2. Click **Create New Jacket**
3. Fill in required information:
   - Staff ID
   - Full name
   - Department
   - Position
   - Date of first appointment
4. Add optional details:
   - Contact information
   - Emergency contact
   - Notes
5. Set initial location (usually Registry)
6. Click **Create Jacket**

## After Creation

- The jacket receives a unique identifier
- Initial documents can be uploaded
- Location tracking begins
- Access logs are recorded

## Best Practices

- Verify staff ID before creation
- Complete all available fields
- Set appropriate initial status
- Upload foundational documents promptly
    `,
  },
  {
    id: 'pfiles-movement',
    title: 'Jacket Movement and Tracking',
    summary: 'How to request and track PFile jacket movements.',
    category: 'pfiles',
    order: 3,
    keywords: ['movement', 'request', 'transfer', 'location', 'track', 'custody'],
    content: `
# Jacket Movement and Tracking

Track and manage the physical movement of personnel file jackets.

## Requesting a Jacket

1. Find the staff member's PFile
2. Click **Request Movement**
3. Provide:
   - Reason for request
   - Expected return date (optional)
   - Priority level
4. Submit the request

## Movement Workflow

1. **Request Submitted**: You submit a request
2. **Pending Approval**: Registry reviews
3. **Approved/Denied**: Decision is made
4. **In Transit**: Physical jacket is being moved
5. **Received**: You confirm receipt
6. **Returned**: Jacket goes back to registry

## Viewing Movement History

1. Open the PFile jacket
2. Click the **Movement History** tab
3. See all movements with:
   - Date and time
   - From/To locations
   - Custodian names
   - Purpose

## Returning a Jacket

1. Open the jacket in your custody
2. Click **Return Jacket**
3. Confirm the return
4. Registry acknowledges receipt

## Best Practices

- Return jackets promptly when done
- Keep accurate records of custody
- Use the notes field for important context
- Report any issues immediately
    `,
  },
];
